import {
  Component,
  forwardRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BsDatepickerConfig, BsDatepickerDirective } from 'ngx-bootstrap/datepicker';
import * as moment from 'moment';

@Component({
  selector: 'df-datepicker',
  templateUrl: './df-datepicker.component.html',
  styleUrls: ['./df-datepicker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DfDatepickerComponent),
      multi: true,
    },
  ],
})
export class DfDatepickerComponent implements ControlValueAccessor, OnChanges {
  @Input() label = 'Date';
  @Input() min = '';
  @Input() max = '';
  @Input() invalid = false;
  @Input() inputId = 'df-datepicker-input';

  @ViewChild('dp', { static: false }) datepicker?: BsDatepickerDirective;

  dateValue: Date | undefined;
  displayValue = '';
  minDate: Date | undefined;
  maxDate: Date | undefined;
  disabled = false;
  private initialized = false;
  private updatingFromPicker = false;

  bsConfig: Partial<BsDatepickerConfig> = {
    containerClass: 'theme-default',
    dateInputFormat: 'MM/DD/YYYY',
    showWeekNumbers: false,
    adaptivePosition: true,
    isAnimated: true,
    showTodayButton: false,
    showClearButton: false,
  };

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['min']) {
      this.minDate = this.parseDate(this.min);
    }
    if (changes['max']) {
      this.maxDate = this.parseDate(this.max);
    }
  }

  writeValue(value: string | null): void {
    if (value) {
      const parsed = moment(value, 'YYYY-MM-DD', true);
      if (parsed.isValid()) {
        this.dateValue = parsed.toDate();
        this.displayValue = parsed.format('MM/DD/YYYY');
      } else {
        this.dateValue = undefined;
        this.displayValue = '';
      }
    } else {
      this.dateValue = undefined;
      this.displayValue = '';
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onDateChange(date: Date): void {
    if (!this.initialized) {
      this.initialized = true;
      if (!date) return;
    }
    if (date) {
      this.displayValue = moment(date).format('MM/DD/YYYY');
      this.updatingFromPicker = true;
    }
    const str = date ? moment(date).format('YYYY-MM-DD') : '';
    this.onChange(str);
  }

  onKeyDown(event: KeyboardEvent): void {
    const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Enter'];
    if (allowed.includes(event.key) || event.ctrlKey || event.metaKey) return;
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  onInputMask(event: Event): void {
    if (this.updatingFromPicker) {
      this.updatingFromPicker = false;
      return;
    }

    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '').substring(0, 8);

    let formatted = '';
    for (let i = 0; i < digits.length; i++) {
      if (i === 2 || i === 4) formatted += '/';
      formatted += digits[i];
    }

    input.value = formatted;
    this.displayValue = formatted;

    const pos = formatted.length;
    input.setSelectionRange(pos, pos);

    if (digits.length === 8) {
      this.tryParseTypedDate(digits);
    } else {
      this.dateValue = undefined;
      this.onChange('');
    }
  }

  onBlur(): void {
    this.onTouched();
  }

  openCalendar(): void {
    this.datepicker?.toggle();
  }

  private tryParseTypedDate(digits: string): void {
    const mm = parseInt(digits.substring(0, 2), 10);
    const dd = parseInt(digits.substring(2, 4), 10);
    const yyyy = parseInt(digits.substring(4, 8), 10);
    const date = new Date(yyyy, mm - 1, dd);

    const isValid = date.getFullYear() === yyyy
      && date.getMonth() === mm - 1
      && date.getDate() === dd;

    if (isValid && this.isWithinBounds(date)) {
      this.dateValue = date;
      this.onChange(moment(date).format('YYYY-MM-DD'));
    } else {
      this.dateValue = undefined;
      this.onChange('');
    }
  }

  private isWithinBounds(date: Date): boolean {
    if (this.minDate && date < this.minDate) return false;
    if (this.maxDate && date > this.maxDate) return false;
    return true;
  }

  private parseDate(value: string): Date | undefined {
    if (!value) return undefined;
    const parsed = moment(value, 'YYYY-MM-DD', true);
    return parsed.isValid() ? parsed.toDate() : undefined;
  }
}
